import { useRouter } from 'next/router';
import React from 'react';
import styled from 'styled-components';

import BreadCrumbs from '@/components/atom/BreadCrumbs';
import MaterialIcon from '@/components/atom/MaterialIcon';
import Chevron from '@/components/atom/icon/Chevron';
import Phone from '@/components/atom/icon/Phone';
import NonF2fDiagnosisBannerIcon from '@/components/atom/icon/hospitals/NonF2fDiagnosisBannerIcon';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import HeaderSearchBar from '@/components/molecules/HeaderSearchBar';
import HospitalList from '@/components/templates/f2f/common/HospitalList';
import HospitalRegionFilterModal from '@/components/templates/f2f/common/HospitalRegionFilterModal';
import SearchHospitalResultEmpty from '@/components/templates/f2f/common/SearchHospitalResultEmpty';
import StickyMoveTopButton from '@/components/templates/mobile/f2f/hospital/HospitalsMobileTemplate/StickyMoveTopButton';
import useHospitalsSearchViewModel from '@/hooks/hospital/search/useHospitalsSearchViewModel';
import theme from '@/styles/theme';
import { CustomPageProps } from '@/types/app';
import { DIRECTION, ROUTES } from '@/types/common';
import { HeaderType } from '@/types/header';
import { ContractType, HospitalCommonFilterType } from '@/types/hospital';
// eslint-disable-next-line import/order
import { addQueryString } from '@/utils/url';

// eslint-disable-next-line import/order
import HospitalSearchEventItem from '../HospitalsSubCategoryTemplate/HospitalSearchEventItem';
import HospitalSearchFilters from './HospitalSearchFilters';
import SearchEntireFilterBottomSheet from './SearchEntireFilterBottomSheet';
import SearchSortTypeBottomSheet from './SearchSortTypeBottomSheet';

interface Props extends CustomPageProps {
  viewModel: ReturnType<typeof useHospitalsSearchViewModel>;
}

const HospitalsSearchTemplate = ({ client, viewModel }: Props) => {
  const router = useRouter();
  const queries = router.query;

  return (
    <TemplateLayout.Wrapper>
      <BreadCrumbsWrapper>
        <BreadCrumbs
          visible={!client.isMobile}
          type='hospitals'
          path={ROUTES.HOSPITAL.SEARCH.path + addQueryString(queries)}
        />
      </BreadCrumbsWrapper>

      <TemplateLayout.StickyHeader type={HeaderType.WithMobileHeader}>
        <HeaderSearchBar
          placeholder={'지역+과목명, 병원명을 입력해주세요'}
          onChange={(e) => viewModel.handleInputChange(e.target.value)}
          value={viewModel.inputValue || ''}
          onSubmit={viewModel.handleChangeKeyword}
          searchInputRef={viewModel.searchInputRef}
        />
      </TemplateLayout.StickyHeader>
      <TemplateLayout.Content>
        <HospitalSearchFilters>
          <HospitalSearchFilters.EntireFilter
            count={viewModel.filterCount}
            onClick={viewModel.toggleSearchEntireFilterBottomSheet}
          />
          <HospitalSearchFilters.ScrollFilters>
            <HospitalSearchFilters.HospitalFilter onClick={viewModel.toggleSortTypeBottomSheet}>
              <span>{viewModel.selectedSortFilter.getTitle()}</span>
              <RotationIcon>
                <MaterialIcon name='chevron_right' size={16} color={theme.color.gray700} />
              </RotationIcon>
            </HospitalSearchFilters.HospitalFilter>
            {viewModel.isDefaultHospitalNearMode() && (
              <HospitalSearchFilters.HospitalFilter
                isSelected={viewModel.isLocationSelected() && viewModel.isDefaultHospitalNearMode()}
                onClick={viewModel.switchRegionFilter}
              >
                <MaterialIcon
                  name='my_location'
                  size={16}
                  color={
                    viewModel.isLocationSelected() && viewModel.isDefaultHospitalNearMode()
                      ? theme.color.primary600
                      : theme.color.gray700
                  }
                  filled
                />
                <span>{viewModel.getHeaderAddress()}</span>
              </HospitalSearchFilters.HospitalFilter>
            )}
            <HospitalSearchFilters.HospitalFilter
              isSelected={viewModel.hospitalCommonFilter.isSelected(HospitalCommonFilterType.NonF2f)}
              onClick={viewModel.toggleHospitalFilter(HospitalCommonFilterType.NonF2f)}
            >
              <Phone
                size={16}
                fill={
                  viewModel.hospitalCommonFilter.isSelected(HospitalCommonFilterType.NonF2f)
                    ? theme.color.primary600
                    : theme.color.gray700
                }
              />
              <span>{viewModel.hospitalCommonFilter.getTitle(HospitalCommonFilterType.NonF2f)}</span>
            </HospitalSearchFilters.HospitalFilter>

            <HospitalSearchFilters.HospitalFilter
              isSelected={viewModel.hospitalCommonFilter.isSelected(HospitalCommonFilterType.Operating)}
              onClick={viewModel.toggleHospitalFilter(HospitalCommonFilterType.Operating)}
            >
              <span>{viewModel.hospitalCommonFilter.getTitle(HospitalCommonFilterType.Operating)}</span>
            </HospitalSearchFilters.HospitalFilter>
            <HospitalSearchFilters.HospitalFilter
              isSelected={viewModel.hospitalCommonFilter.isSelected(HospitalCommonFilterType.NightOperation)}
              onClick={viewModel.toggleHospitalFilter(HospitalCommonFilterType.NightOperation)}
            >
              <span>{viewModel.hospitalCommonFilter.getTitle(HospitalCommonFilterType.NightOperation)}</span>
            </HospitalSearchFilters.HospitalFilter>
            <HospitalSearchFilters.HospitalFilter
              isSelected={viewModel.hospitalCommonFilter.isSelected(HospitalCommonFilterType.Specialist)}
              onClick={viewModel.toggleHospitalFilter(HospitalCommonFilterType.Specialist)}
            >
              <span>{viewModel.hospitalCommonFilter.getTitle(HospitalCommonFilterType.Specialist)}</span>
            </HospitalSearchFilters.HospitalFilter>
          </HospitalSearchFilters.ScrollFilters>
        </HospitalSearchFilters>
        {!!viewModel.keyword && (
          <>
            <HospitalList
              isLoading={viewModel.isLoadingHospitals}
              fetchNextPage={viewModel.fetchNextPage}
              hasNextPage={viewModel.hasNextPage}
              hasHospitals={!!viewModel.hospitals.length}
            >
              {viewModel.hospitals.map((hospital, index) => {
                return (
                  <>
                    <HospitalSearchEventItem
                      key={`event-${hospital.hospitalPublicId}`}
                      onClickAdBanner={viewModel.goToEventPage}
                      onClickMoveToDoctorDetail={() => viewModel.goToAppHospital(hospital.hospitalPublicId)}
                      doctorDetailPageHref={`${ROUTES.HOSPITAL.DETAIL.path}${hospital.hospitalPublicId}`}
                      index={index}
                      adBanners={hospital.hospitalAdBanners}
                      hospitalPid={hospital.hospitalPublicId}
                      status={hospital.hospitalOperationStatus}
                      name={hospital.hospitalName}
                      distance={hospital.hospitalDistance}
                      rating={hospital.reviewScore}
                      reviewTotalCount={hospital.reviewTotalCount}
                      badges={hospital.hospitalBadges}
                      imageUrl={hospital.hospitalRepresentativeImageUrl}
                      address={hospital.hospitalAddress}
                      isBookable={hospital.isBookable}
                      representativeDepartmentName={hospital.hospitalRepresentativeDepartmentName}
                      operationDisplay={hospital.operationDisplay}
                      hasNonF2fDoctors={!!hospital.doctors.find((doctor) => doctor.contractType === ContractType.All)}
                    />
                    {index === 1 && viewModel.matchedCategory && (
                      <NonF2fDoctorListBannerWrapper>
                        <NonF2fDoctorListBanner onClick={viewModel.clickNonF2fDoctorListBanner}>
                          <NonF2fDoctorListBannerContent>
                            <NonF2fDiagnosisBannerIcon />
                            <NonF2fDcotorListBannerTextArea>
                              <NonF2fDoctorListBannerTitle>집에서 편하게 비대면 진료 받기</NonF2fDoctorListBannerTitle>
                              <NonF2fDoctorListBannerDescription>
                                병원 가지 않아도 진료를 볼 수 있어요
                              </NonF2fDoctorListBannerDescription>
                            </NonF2fDcotorListBannerTextArea>
                          </NonF2fDoctorListBannerContent>
                          <Chevron direction={DIRECTION.RIGHT} width={12} height={12} color={theme.color.gray300} />
                        </NonF2fDoctorListBanner>
                      </NonF2fDoctorListBannerWrapper>
                    )}
                  </>
                );
              })}
            </HospitalList>
            <StickyMoveTopButton />
          </>
        )}
        {viewModel.isVisibleDeleteIcon && !viewModel.isLoadingHospitals && viewModel.hasNoData && (
          <SearchHospitalResultEmpty />
        )}
      </TemplateLayout.Content>
      {viewModel.isSortTypeBottomSheetOpen && (
        <SearchSortTypeBottomSheet
          sortFilterList={viewModel.sortFilterList}
          selectedSortFilter={viewModel.selectedSortFilter}
          toggleModal={viewModel.toggleSortTypeBottomSheet}
          onClick={viewModel.selectHospitalSortFilter}
        />
      )}
      {viewModel.isRegionFilterOpened && (
        <HospitalRegionFilterModal
          {...viewModel.getRegionFilterProps()}
          isOpened={viewModel.isRegionFilterOpened}
          client={client}
        />
      )}
      {viewModel.isSearchEntireFilterBottomSheet && (
        <SearchEntireFilterBottomSheet {...viewModel.getSearchEntireFilterBottomSheetProps()} client={client} />
      )}
    </TemplateLayout.Wrapper>
  );
};

export default HospitalsSearchTemplate;

const BreadCrumbsWrapper = styled.div`
  padding: ${({ theme }) => (theme.client.isMobile ? '0' : '24px 24px 16px')};
`;

const RotationIcon = styled.div`
  transform: rotate(90deg);
`;

const NonF2fDoctorListBannerWrapper = styled.div`
  display: flex;
  width: 100%;
  padding: 20px;
  flex-direction: column;
  align-items: flex-start;
`;

const NonF2fDoctorListBanner = styled.div`
  padding: 16px;
  align-items: center;
  align-self: stretch;
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  flex: 1 0 0;
  border-radius: 8px;
  background: ${({ theme }) => theme.color.gray40};
`;

const NonF2fDoctorListBannerContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;

const NonF2fDcotorListBannerTextArea = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const NonF2fDoctorListBannerTitle = styled.div`
  ${({ theme }) => `
      ${theme.typography.UI14M};
      color: ${theme.color.gray800};
    `}
`;
const NonF2fDoctorListBannerDescription = styled.div`
  ${({ theme }) => `
      ${theme.typography.UI12R};
      color: ${theme.color.gray500}
    `}
`;
